/**
 * Named motion presets for Leviathan.
 *
 * Every preset is a framer-motion-compatible set of props. Importing
 * consumers should spread the preset onto a `motion.*` element. The
 * presets respect `prefers-reduced-motion` via the `reducedMotion`
 * helper at the bottom; any consumer that cannot use the `useReducedMotion`
 * hook should call `reducedMotion(preset)` to get a motion-free variant.
 *
 * Motion vocabulary:
 *   - `fadeIn` / `fadeInUp` / `fadeInDown` / `fadeInLeft` / `fadeInRight`
 *     simple entrance fades with optional direction.
 *   - `staggerChildren`  parent preset that staggers its children per `motion.stagger`.
 *   - `glassReveal`  the hero glass-card entrance; slightly springy.
 *   - `tableRowReveal`  subtle fade for data table rows.
 *   - `tabContentSwitch`  used for in-place tab content changes.
 *   - `sidebarFlyout`  sidebar submenu or flyout animation.
 *   - `dialogEnter` / `dialogExit`  modal surface animation.
 *   - `popoverEnter`  dropdown / tooltip surface animation.
 *   - `buttonPress`  tiny scale on press (used with `whileTap`).
 *   - `cardHover`  subtle lift used with `whileHover`.
 *
 * Do not add em-dashes anywhere.
 */

import type { Transition, Variants } from 'framer-motion'
import { motion as motionTokens } from '@/design-system/tokens'

const standardTransition: Transition = {
  duration: motionTokens.duration.base / 1000,
  ease: motionTokens.ease.standard,
}

const entranceTransition: Transition = {
  duration: motionTokens.duration.slow / 1000,
  ease: motionTokens.ease.entrance,
}

const emphasizedTransition: Transition = {
  duration: motionTokens.duration.slower / 1000,
  ease: motionTokens.ease.emphasized,
}

export const fadeIn = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: standardTransition,
}

export const fadeInUp = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 12 },
  transition: entranceTransition,
}

export const fadeInDown = {
  initial: { opacity: 0, y: -12 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -12 },
  transition: entranceTransition,
}

export const fadeInLeft = {
  initial: { opacity: 0, x: -12 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -12 },
  transition: entranceTransition,
}

export const fadeInRight = {
  initial: { opacity: 0, x: 12 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 12 },
  transition: entranceTransition,
}

export const staggerChildren: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: motionTokens.stagger.base,
      delayChildren: 0.08,
    },
  },
}

export const staggerChildrenTight: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: motionTokens.stagger.tight,
    },
  },
}

export const glassReveal = {
  initial: { opacity: 0, y: 16, scale: 0.98 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: 16, scale: 0.98 },
  transition: emphasizedTransition,
}

export const tableRowReveal = {
  initial: { opacity: 0, y: 4 },
  animate: { opacity: 1, y: 0 },
  transition: {
    duration: motionTokens.duration.fast / 1000,
    ease: motionTokens.ease.entrance,
  },
}

export const tabContentSwitch = {
  initial: { opacity: 0, y: 6 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -6 },
  transition: {
    duration: motionTokens.duration.base / 1000,
    ease: motionTokens.ease.standard,
  },
}

export const sidebarFlyout = {
  initial: { opacity: 0, x: -8 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -8 },
  transition: standardTransition,
}

export const dialogEnter = {
  initial: { opacity: 0, y: 12, scale: 0.96 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: 12, scale: 0.96 },
  transition: emphasizedTransition,
}

export const popoverEnter = {
  initial: { opacity: 0, y: -4, scale: 0.98 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: -4, scale: 0.98 },
  transition: {
    duration: motionTokens.duration.fast / 1000,
    ease: motionTokens.ease.standard,
  },
}

export const buttonPress = {
  whileTap: { scale: 0.97 },
  transition: { duration: motionTokens.duration.instant / 1000 },
}

export const cardHover = {
  whileHover: { y: -2, transition: { duration: motionTokens.duration.fast / 1000 } },
}

type AnyMotionProps = Record<string, unknown>

/**
 * Strip initial/animate/exit variants from a motion preset so it renders
 * without animation. Used when `prefers-reduced-motion: reduce` is active
 * and the caller cannot use the `useReducedMotion` hook directly.
 *
 * The transition prop is preserved so non-animating property changes
 * (e.g. color transitions on hover) still feel smooth.
 */
export function reducedMotion<T extends AnyMotionProps>(preset: T): T {
  const { initial: _i, animate: _a, exit: _e, variants: _v, ...rest } = preset as Record<
    string,
    unknown
  >
  return rest as T
}
